import * as MIDI from "midicube"
import { ScaleType, ModeType, getActiveIntervals } from "./music-theory"

// Track audio context state for mobile browsers
let audioContextInitialized = false

// ===== INSTRUMENTOS DISPONIBLES =====
export interface InstrumentSet {
   solo: string
   accompaniment: string
   bass: string
   name: string
   description: string
}

// Catálogo completo de instrumentos
export const AVAILABLE_INSTRUMENTS: Record<string, InstrumentSet> = {
   classical: {
      solo: "acoustic_grand_piano",
      accompaniment: "string_ensemble_1",
      bass: "contrabass",
      name: "Clásica",
      description: "Piano, Cuerdas y Contrabajo"
   },
   piano: {
      solo: "acoustic_grand_piano",
      accompaniment: "bright_acoustic_piano",
      bass: "electric_piano_1",
      name: "Piano",
      description: "Tres tipos de piano"
   },
   guitar: {
      solo: "acoustic_guitar_nylon",
      accompaniment: "acoustic_guitar_steel",
      bass: "acoustic_bass",
      name: "Guitarra",
      description: "Guitarras acústicas y bajo"
   },
   strings: {
      solo: "violin",
      accompaniment: "string_ensemble_1",
      bass: "cello",
      name: "Cuerdas",
      description: "Violín, Ensemble y Cello"
   },
   flute: {
      solo: "flute",
      accompaniment: "clarinet",
      bass: "bassoon",
      name: "Viento Madera",
      description: "Flauta, Clarinete y Fagot"
   },
   harpsichord: {
      solo: "harpsichord",
      accompaniment: "string_ensemble_1",
      bass: "contrabass",
      name: "Barroca",
      description: "Clavecín con cuerdas"
   },
   cinematic: {
      solo: "violin",
      accompaniment: "string_ensemble_1",
      bass: "trombone",
      name: "Cinematográfica",
      description: "Cuerdas y viento metal"
   },
   jazz: {
      solo: "acoustic_grand_piano",
      accompaniment: "electric_piano_1",
      bass: "acoustic_bass",
      name: "Jazz",
      description: "Piano, Rhodes y Bajo acústico"
   },
   orchestral: {
      solo: "trumpet",
      accompaniment: "french_horn",
      bass: "tuba",
      name: "Orquestal",
      description: "Trompeta, Corno y Tuba"
   },
   synth: {
      solo: "lead_1_square",
      accompaniment: "pad_2_warm",
      bass: "synth_bass_1",
      name: "Sintetizador",
      description: "Sonidos electrónicos"
   },
   organ: {
      solo: "church_organ",
      accompaniment: "reed_organ",
      bass: "tremolo_strings",
      name: "Órgano",
      description: "Órganos y cuerdas trémolo"
   },
}

// Set actual (configurable)
let currentInstruments: InstrumentSet = AVAILABLE_INSTRUMENTS.classical
let loadedInstruments: Set<string> = new Set()

// Configuración de volumen
let masterVolume = 1.0
let channelVolumes = [110, 85, 95] // Melodía, acompañamiento, bajo

// Configuración de escala actual
let currentScale: ScaleType = "major"
let currentMode: ModeType = "ionian"
let currentRootNote = 60 // C4

export function setInstrumentSet(setName: string) {
   if (AVAILABLE_INSTRUMENTS[setName]) {
      currentInstruments = AVAILABLE_INSTRUMENTS[setName]
      loadInstrumentsForCurrentSet()
   }
}

export function getCurrentInstruments(): InstrumentSet {
   return currentInstruments
}

export function getAvailableInstrumentSets(): string[] {
   return Object.keys(AVAILABLE_INSTRUMENTS)
}

export function setMasterVolume(volume: number) {
   masterVolume = Math.max(0, Math.min(1, volume))
   if (window.MIDI?.setMasterVolume) {
      window.MIDI.setMasterVolume(masterVolume)
   }
}

export function setChannelVolume(channel: number, volume: number) {
   channelVolumes[channel] = Math.max(0, Math.min(127, volume))
   if (window.MIDI?.setVolume) {
      window.MIDI.setVolume(channel, channelVolumes[channel])
   }
}

export function getChannelVolume(channel: number): number {
   return channelVolumes[channel] || 100
}

export function setScale(scale: ScaleType, mode: ModeType, rootNote?: number) {
   currentScale = scale
   currentMode = mode
   if (rootNote !== undefined) {
      currentRootNote = rootNote
   }
}

export function getCurrentScale() {
   return { scale: currentScale, mode: currentMode, rootNote: currentRootNote }
}

// Carga instrumentos dinámicamente
function loadInstrumentsForCurrentSet() {
   const instrumentsToLoad = [
      currentInstruments.solo,
      currentInstruments.accompaniment,
      currentInstruments.bass,
   ]
   
   const uniqueInstruments = Array.from(new Set(instrumentsToLoad)) as string[]
   
   uniqueInstruments.forEach((instrumentName, index) => {
      if (!loadedInstruments.has(instrumentName) && window.MIDI?.programChange && window.MIDI?.GM?.byName) {
         const program = window.MIDI.GM.byName[instrumentName]?.program
         if (program !== undefined) {
            window.MIDI.programChange(index, program)
            loadedInstruments.add(instrumentName)
            console.log(`Loaded instrument: ${instrumentName} on channel ${index}`)
         }
      }
   })
}

export function start() {
   // Ensure audio context is resumed (required for mobile browsers)
   if (window.MIDI?.AudioContext) {
      const ctx = window.MIDI.AudioContext
      if (ctx.state === "suspended") {
         ctx.resume().then(() => {
            console.log("AudioContext resumed successfully")
            performStart()
         }).catch((err: Error) => {
            console.error("Failed to resume AudioContext:", err)
         })
         return
      }
   }
   performStart()
}

function performStart() {
   MIDI.setVolume(0, 0, 0)
   MIDI.noteOn(0, 30, 0, 0)
   MIDI.noteOff(0, 30, 0)
   MIDI.setVolume(0, channelVolumes[0], 0)
   audioContextInitialized = true
}

declare global {
   interface Window {
      MIDI: any
   }
}

export function loadMidi(loaded: () => void) {
   // @ts-ignore
   window.MIDI = (MIDI || {}) as any
   
   // Cargar todos los instrumentos necesarios
   const allInstruments = Object.values(AVAILABLE_INSTRUMENTS).flatMap(set => [
      set.solo,
      set.accompaniment,
      set.bass
   ])
   const uniqueInstruments = Array.from(new Set(allInstruments))
   
   MIDI.loadPlugin({
      soundfontUrl: "https://juankysoriano.github.io/midi-js-soundfonts/AirFont/",
      targetFormat: "mp3",
      instrument: uniqueInstruments,
      onsuccess() {
         // Asignar instrumentos iniciales
         const initialSet = currentInstruments
         ;[initialSet.solo, initialSet.accompaniment, initialSet.bass].forEach((instrument, index) => {
            if (window.MIDI?.programChange && window.MIDI?.GM?.byName) {
               const program = window.MIDI.GM.byName[instrument]?.program
               if (program !== undefined) {
                  window.MIDI.programChange(index, program)
                  loadedInstruments.add(instrument)
               }
            }
         })
         
         // Volumen por canal
         MIDI.setVolume(0, channelVolumes[0])  // Melodía
         MIDI.setVolume(1, channelVolumes[1])  // Acompañamiento  
         MIDI.setVolume(2, channelVolumes[2])  // Bajo
         
         // For mobile: ensure AudioContext is created but suspended
         if (window.MIDI?.AudioContext && window.MIDI.AudioContext.state === "running") {
            window.MIDI.AudioContext.suspend()
         }
         
         loaded()
      },
   })
}

// Helper to check if audio is ready (for UI state)
export function isAudioReady(): boolean {
   return audioContextInitialized
}

// Helper to resume audio context (call on any user interaction)
export async function resumeAudioContext(): Promise<boolean> {
   if (window.MIDI?.AudioContext) {
      const ctx = window.MIDI.AudioContext
      if (ctx.state === "suspended") {
         try {
            await ctx.resume()
            console.log("AudioContext resumed")
            return true
         } catch (err) {
            console.error("Failed to resume AudioContext:", err)
            return false
         }
      }
   }
   return true
}

// ===== FUNCIONES DE NOTAS CON ESCALA =====

// Convierte una nota a la escala actual (quantization)
export function quantizeToScale(note: number): number {
   if (currentScale === "chromatic") return note
   
   const intervals = getActiveIntervals(currentScale, currentMode)
   const octave = Math.floor((note - currentRootNote) / 12)
   const semitone = (note - currentRootNote) % 12
   
   // Encontrar el intervalo más cercano
   let closestInterval = intervals[0]
   let minDistance = Math.abs(semitone - intervals[0])
   
   for (const interval of intervals) {
      const distance = Math.abs(semitone - interval)
      if (distance < minDistance) {
         minDistance = distance
         closestInterval = interval
      }
   }
   
   return currentRootNote + closestInterval + (octave * 12)
}

// Notas con instrumentos específicos y quantización a escala
export const noteOnSolo = (note: number, velocity: number) => {
   if (window.MIDI?.AudioContext?.state === "suspended") {
      window.MIDI.AudioContext.resume()
   }
   const quantizedNote = quantizeToScale(note)
   MIDI.noteOn(0, quantizedNote, velocity + randomNumber(-3, 3), 0.02 + randomNumber(-0.02, 0.02))
}

export const noteOnAccompaniment = (note: number, velocity: number) => {
   if (window.MIDI?.AudioContext?.state === "suspended") {
      window.MIDI.AudioContext.resume()
   }
   const quantizedNote = quantizeToScale(note)
   MIDI.noteOn(1, quantizedNote, velocity * 0.8 + randomNumber(-3, 3), 0.02 + randomNumber(-0.02, 0.02))
}

export const noteOnBass = (note: number, velocity: number) => {
   if (window.MIDI?.AudioContext?.state === "suspended") {
      window.MIDI.AudioContext.resume()
   }
   const quantizedNote = quantizeToScale(note)
   MIDI.noteOn(2, quantizedNote, velocity * 0.9 + randomNumber(-2, 2), 0.02 + randomNumber(-0.02, 0.02))
}

// Compatibilidad hacia atrás
export const noteOn = (instrument: number, note: number, velocity: number) => {
   if (window.MIDI?.AudioContext?.state === "suspended") {
      window.MIDI.AudioContext.resume()
   }
   const quantizedNote = currentScale !== "chromatic" ? quantizeToScale(note) : note
   MIDI.noteOn(instrument, quantizedNote, velocity + randomNumber(-3, 3), 0.025 + randomNumber(-0.025, 0.025))
}

export const chordOn = (instrument: number, chord: number[], velocity: number) => {
   if (window.MIDI?.AudioContext?.state === "suspended") {
      window.MIDI.AudioContext.resume()
   }
   const quantizedChord = currentScale !== "chromatic" 
      ? chord.map(n => quantizeToScale(n)) 
      : chord
   MIDI.chordOn(instrument, quantizedChord, velocity + randomNumber(-3, 3), 0.025 + randomNumber(-0.025, 0.025))
}

export const noteOff = (instrument: number, note: number) => {
   MIDI.noteOff(instrument, note, 0.2 + randomNumber(-0.05, 0.05))
}

export const chordOff = (instrument: number, chord: number[]) => {
   MIDI.chordOff(instrument, chord, 0.2 + randomNumber(-0.05, 0.05))
}

const randomNumber = (min: number, max: number) => Math.random() * (max - min) + min
